/**
 * Member Payouts Component
 * Lists and manages member incentive payouts with approval workflow
 * Trust Marketplace - Phase 2: Financial Ledger System
 */

import React, { useState, useEffect } from 'react';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { getWeaveinApiUrl } from '@/src/config';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { CardIssuer } from './CardIssuer';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import {
  PAYOUT_STATUS_FILTER_OPTIONS,
  PayoutStatusIcon,
  countWhere,
  isAwaitingApproval,
  isFailed,
  isInFlight,
  isPaid,
  payoutStatusColor,
  payoutStatusLabel,
  sumWhere,
} from './payoutStatus';
import { PayoutDeferralNotice, type PayoutDeferral } from './PayoutDeferralNotice';
import {
  DollarSign,
  Users,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Filter,
  Download,
  CreditCard,
  AlertCircle,
  Plus
} from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface MemberPayoutsProps {
  memberId?: number;
  programId?: number;
}

interface MemberPayout {
  id: number;
  payout_number: string;
  member_id: number;
  member_name?: string;
  payout_amount: number;
  payout_method: string;
  status: string;
  approval_status?: string;
  initiated_date: string;
  scheduled_date?: string;
  processed_date?: string;
  fund_source_id?: number;
  fund_source_number?: string;
  payment_processor_reference?: string;
  approval_notes?: string;
  created_at: string;
  member_payment_account_id?: number;
  payment_account_type?: string;
  payment_account_last4?: string;
  payment_card_network?: string;
  org_bank_account_id?: number;
  org_bank_account_name?: string;
  org_bank_account_last4?: string;
  /** Present while a payment is held out of a period that was already full. */
  deferral?: PayoutDeferral | null;
}

const PAYOUT_METHODS = [
  { value: 'CARD', label: 'Prepaid Card' },
  { value: 'DIRECT_DEPOSIT', label: 'Direct Deposit' },
  { value: 'CHECK', label: 'Check' },
  { value: 'CASH', label: 'Cash' }
];



const MemberPayouts: React.FC<MemberPayoutsProps> = ({ memberId, programId }) => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const { fmt } = useFormattedDate();
  const [payouts, setPayouts] = useState<MemberPayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterMethod, setFilterMethod] = useState<string>('');
  const [filterMember, setFilterMember] = useState<string>(memberId?.toString() || '');

  // Modal states
  const [selectedPayout, setSelectedPayout] = useState<MemberPayout | null>(null);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');
  const [approvalNotes, setApprovalNotes] = useState('');

  // Payment method management
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] = useState(false);
  const [selectedMemberForPayment, setSelectedMemberForPayment] = useState<{id: number, name: string} | null>(null);
  const [isCardIssuerOpen, setIsCardIssuerOpen] = useState(false);

  useEffect(() => {
    fetchPayouts();
  }, [memberId, programId, filterStatus, filterMethod, filterMember]);

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterMember) params.append('member_id', filterMember);
      if (programId) params.append('program_id', programId.toString());
      if (filterStatus) params.append('status', filterStatus);
      if (filterMethod) params.append('payout_method', filterMethod);

      const url = getWeaveinApiUrl(`/api/member-incentive-payouts?${params.toString()}`);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch payouts: ${response.status}`);
      }

      const data = await response.json();
      setPayouts(data);
    } catch (err) {
      console.error('Error fetching payouts:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalAction = async () => {
    if (!selectedPayout) return;

    try {
      const action = approvalAction === 'approve' ? 'approve' : 'reject';
      const url = getWeaveinApiUrl(`/api/member-incentive-payouts/${selectedPayout.id}/${action}`);

      const response = await authFetch(url, {
        method: 'PUT',
        jsonBody: {
          notes: approvalNotes
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} payout`);
      }

      // Reset and refresh
      setApprovalNotes('');
      setIsApprovalModalOpen(false);
      setSelectedPayout(null);
      fetchPayouts();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to process approval');
    }
  };

  const openApprovalModal = (payout: MemberPayout, action: 'approve' | 'reject') => {
    setSelectedPayout(payout);
    setApprovalAction(action);
    setIsApprovalModalOpen(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return fmt.dateTime(dateString);
  };

  // Calculate summary metrics
  const totalAmount = payouts.reduce((sum, p) => sum + p.payout_amount, 0);
  // Scheduled money counts as outstanding: 'deferred' is a payment with a date,
  // not a payment that failed, so it belongs with pending rather than nowhere.
  const pendingAmount = sumWhere(payouts, isInFlight);
  const completedAmount = sumWhere(payouts, isPaid);
  const pendingCount = countWhere(payouts, isInFlight);
  const paidCount = countWhere(payouts, isPaid);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <p className="text-red-800">Error: {error}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        {!tabbed && <h2 className="text-2xl font-bold text-gray-900">{t('financial.member_payouts', 'Member Payouts')}</h2>}
        <p className="text-sm text-gray-600 mt-1">{t('financial.track_and_approve_member_incentive', 'Track and approve member incentive payments')}</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-700 font-medium">{t('financial.total_payouts', 'Total Payouts')}</span>
          </div>
          <div className="text-2xl font-bold text-blue-900">{formatCurrency(totalAmount)}</div>
          <div className="text-xs text-blue-600 mt-1">{payouts.length} transactions</div>
        </Card>

        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-yellow-600" />
            <span className="text-xs text-yellow-700 font-medium">{t('financial.pending', 'Pending')}</span>
          </div>
          <div className="text-2xl font-bold text-yellow-900">{formatCurrency(pendingAmount)}</div>
          <div className="text-xs text-yellow-600 mt-1">{pendingCount} awaiting approval</div>
        </Card>

        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700 font-medium">{t('financial.completed', 'Completed')}</span>
          </div>
          <div className="text-2xl font-bold text-green-900">{formatCurrency(completedAmount)}</div>
          <div className="text-xs text-green-600 mt-1">
            {paidCount} paid out
          </div>
        </Card>

        <Card className="p-4 bg-purple-50 border-purple-200">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-purple-600" />
            <span className="text-xs text-purple-700 font-medium">{t('financial.recipients', 'Recipients')}</span>
          </div>
          <div className="text-2xl font-bold text-purple-900">
            {new Set(payouts.map(p => p.member_id)).size}
          </div>
          <div className="text-xs text-purple-600 mt-1">unique members</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="filter_status">{t('financial.status', 'Status')}</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t('financial.all_statuses_1', 'All Statuses')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t('financial.all_statuses', 'All Statuses')}</SelectItem>
                {PAYOUT_STATUS_FILTER_OPTIONS.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="filter_method">{t('financial.payment_method', 'Payment Method')}</Label>
            <Select value={filterMethod} onValueChange={setFilterMethod}>
              <SelectTrigger>
                <SelectValue placeholder={t('financial.all_methods_1', 'All Methods')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t('financial.all_methods', 'All Methods')}</SelectItem>
                {PAYOUT_METHODS.map(method => (
                  <SelectItem key={method.value} value={method.value}>
                    {method.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2 flex items-end gap-2">
            <Button variant="outline" onClick={fetchPayouts} className="flex-1">
              <Filter className="w-4 h-4 mr-2" />
              {t('financial.apply_filters', 'Apply Filters')}
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              {t('financial.export', 'Export')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Payouts List */}
      {payouts.length === 0 ? (
        <Card className="p-12 text-center">
          <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('financial.no_payouts_found', 'No payouts found')}</h3>
          <p className="text-sm text-gray-600">
            {t('financial.member_payouts_will_appear_here', 'Member payouts will appear here as incentives are earned')}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {payouts.map(payout => (
            <Card key={payout.id} className="p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <PayoutStatusIcon status={payout.status} />
                </div>

                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {formatCurrency(payout.payout_amount)}
                        </h4>
                        <Badge className={payoutStatusColor(payout.status)}>
                          {payoutStatusLabel(payout.status)}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600 flex-wrap">
                        <span>{payout.payout_number}</span>
                        {payout.member_name && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {payout.member_name}
                            </span>
                          </>
                        )}
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <CreditCard className="w-3 h-3" />
                          {PAYOUT_METHODS.find(m => m.value === payout.payout_method)?.label}
                        </span>
                        {payout.payment_account_last4 && (
                          <>
                            <span>•</span>
                            <span className="text-xs">
                              {payout.payment_card_network || payout.payment_account_type} ...{payout.payment_account_last4}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {isAwaitingApproval(payout.status) && (
                        <>
                          <Button
                            size="sm"
                            className="bg-v2accent hover:bg-v2accent/90"
                            onClick={() => openApprovalModal(payout, 'approve')}
                          >
                            <ThumbsUp className="w-4 h-4 mr-1" />
                            {t('financial.approve', 'Approve')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => openApprovalModal(payout, 'reject')}
                          >
                            <ThumbsDown className="w-4 h-4 mr-1" />
                            {t('financial.reject', 'Reject')}
                          </Button>
                        </>
                      )}
                      <Dialog>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedPayout(payout)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {t('financial.details', 'Details')}
                        </Button>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>{t('financial.payout_details', 'Payout Details')}</DialogTitle>
                          </DialogHeader>
                          {selectedPayout && selectedPayout.id === payout.id && (
                            <div className="space-y-4 mt-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-xs text-gray-500">{t('financial.payout_number', 'Payout Number')}</Label>
                                  <p className="font-medium">{selectedPayout.payout_number}</p>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500">{t('financial.status_1', 'Status')}</Label>
                                  <Badge className={payoutStatusColor(selectedPayout.status)}>
                                    {payoutStatusLabel(selectedPayout.status)}
                                  </Badge>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500">{t('financial.amount', 'Amount')}</Label>
                                  <p className="text-lg font-bold text-green-600">
                                    {formatCurrency(selectedPayout.payout_amount)}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500">{t('financial.payment_method_1', 'Payment Method')}</Label>
                                  <p className="font-medium">
                                    {PAYOUT_METHODS.find(m => m.value === selectedPayout.payout_method)?.label}
                                  </p>
                                </div>
                                {selectedPayout.payment_account_type && (
                                  <div className="col-span-2">
                                    <Label className="text-xs text-gray-500">{t('financial.payment_account', 'Payment Account')}</Label>
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium">
                                        {selectedPayout.payment_account_type.replace('_', ' ')}
                                        {selectedPayout.payment_account_last4 && ` ending in ${selectedPayout.payment_account_last4}`}
                                        {selectedPayout.payment_card_network && ` (${selectedPayout.payment_card_network})`}
                                      </p>
                                    </div>
                                  </div>
                                )}
                                {selectedPayout.org_bank_account_name && (
                                  <div className="col-span-2">
                                    <Label className="text-xs text-gray-500">{t('financial.source_bank_account', 'Source Bank Account')}</Label>
                                    <p className="font-medium">
                                      {selectedPayout.org_bank_account_name}
                                      {selectedPayout.org_bank_account_last4 && (
                                        <span className="text-gray-500 ml-2">(...{selectedPayout.org_bank_account_last4})</span>
                                      )}
                                    </p>
                                  </div>
                                )}
                                <div>
                                  <Label className="text-xs text-gray-500">{t('financial.initiated_date', 'Initiated Date')}</Label>
                                  <p className="text-sm">{formatDate(selectedPayout.initiated_date)}</p>
                                </div>
                                {selectedPayout.scheduled_date && (
                                  <div>
                                    <Label className="text-xs text-gray-500">{t('financial.scheduled_date', 'Scheduled Date')}</Label>
                                    <p className="text-sm">{formatDate(selectedPayout.scheduled_date)}</p>
                                  </div>
                                )}
                                {selectedPayout.processed_date && (
                                  <div>
                                    <Label className="text-xs text-gray-500">{t('financial.processed_date', 'Processed Date')}</Label>
                                    <p className="text-sm">{formatDate(selectedPayout.processed_date)}</p>
                                  </div>
                                )}
                                {selectedPayout.fund_source_number && (
                                  <div>
                                    <Label className="text-xs text-gray-500">{t('financial.fund_source', 'Fund Source')}</Label>
                                    <p className="text-sm font-mono">{selectedPayout.fund_source_number}</p>
                                  </div>
                                )}
                                {selectedPayout.payment_processor_reference && (
                                  <div className="col-span-2">
                                    <Label className="text-xs text-gray-500">{t('financial.payment_reference', 'Payment Reference')}</Label>
                                    <p className="text-sm font-mono">{selectedPayout.payment_processor_reference}</p>
                                  </div>
                                )}
                                {selectedPayout.approval_notes && (
                                  <div className="col-span-2">
                                    <Label className="text-xs text-gray-500">{t('financial.approval_notes', 'Approval Notes')}</Label>
                                    <p className="text-sm text-gray-700">{selectedPayout.approval_notes}</p>
                                  </div>
                                )}
                              </div>

                              <div className="flex justify-end gap-2 pt-4 border-t">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedMemberForPayment({
                                      id: selectedPayout.member_id,
                                      name: selectedPayout.member_name || 'Member'
                                    });
                                    setIsPaymentMethodModalOpen(true);
                                  }}
                                >
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  {t('financial.manage_payment_methods', 'Manage Payment Methods')}
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Initiated: {formatDate(payout.initiated_date)}</span>
                    {payout.scheduled_date && (
                      <>
                        <span>→</span>
                        <span>Scheduled: {formatDate(payout.scheduled_date)}</span>
                      </>
                    )}
                    {payout.processed_date && (
                      <>
                        <span>→</span>
                        <span className="text-green-600 font-medium">
                          Processed: {formatDate(payout.processed_date)}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Additional Info */}
                  {payout.payment_processor_reference && (
                    <div className="mt-2 text-xs text-gray-500">
                      Ref: {payout.payment_processor_reference}
                    </div>
                  )}

                  {payout.deferral && <PayoutDeferralNotice deferral={payout.deferral} />}

                  {isFailed(payout.status) && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      <span className="text-sm text-red-800">
                        {t('financial.payment_failed_please_review_and', 'Payment failed. Please review and retry.')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Payment Method Management Modal */}
      {selectedMemberForPayment && (
        <Dialog open={isPaymentMethodModalOpen} onOpenChange={setIsPaymentMethodModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Manage Payment Methods for {selectedMemberForPayment.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <PaymentMethodSelector
                memberId={selectedMemberForPayment.id}
                label={t('financial.member_payment_methods', 'Member Payment Methods')}
                showIssueCard={true}
                onIssueCard={() => {
                  setIsCardIssuerOpen(true);
                  setIsPaymentMethodModalOpen(false);
                }}
              />
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={() => setIsPaymentMethodModalOpen(false)}>
                  {t('financial.close', 'Close')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Card Issuer Modal */}
      {selectedMemberForPayment && (
        <CardIssuer
          memberId={selectedMemberForPayment.id}
          memberName={selectedMemberForPayment.name}
          isOpen={isCardIssuerOpen}
          onClose={() => {
            setIsCardIssuerOpen(false);
            setIsPaymentMethodModalOpen(true); // Go back to payment method selector
          }}
          onCardIssued={() => {
            // Refresh payment methods
            setIsPaymentMethodModalOpen(true);
          }}
        />
      )}

      {/* Approval Modal */}
      <Dialog open={isApprovalModalOpen} onOpenChange={setIsApprovalModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {approvalAction === 'approve' ? 'Approve' : 'Reject'} Payout
            </DialogTitle>
          </DialogHeader>
          {selectedPayout && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">{t('financial.amount_1', 'Amount:')}</span>
                  <span className="text-lg font-bold">{formatCurrency(selectedPayout.payout_amount)}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">{t('financial.member', 'Member:')}</span>
                  <span className="font-medium">{selectedPayout.member_name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('financial.method', 'Method:')}</span>
                  <span className="font-medium">
                    {PAYOUT_METHODS.find(m => m.value === selectedPayout.payout_method)?.label}
                  </span>
                </div>
              </div>

              <div>
                <Label htmlFor="approval_notes">
                  {approvalAction === 'approve' ? 'Approval' : 'Rejection'} Notes
                </Label>
                <Textarea
                  id="approval_notes"
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder={`Enter notes for ${approvalAction === 'approve' ? 'approval' : 'rejection'}...`}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsApprovalModalOpen(false)}>
                  {t('financial.cancel', 'Cancel')}
                </Button>
                <Button
                  onClick={handleApprovalAction}
                  className={
                    approvalAction === 'approve'
                      ? 'bg-v2accent hover:bg-v2accent/90'
                      : 'bg-red-600 hover:bg-red-700'
                  }
                >
                  {approvalAction === 'approve' ? (
                    <>
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      {t('financial.approve_payout', 'Approve Payout')}
                    </>
                  ) : (
                    <>
                      <ThumbsDown className="w-4 h-4 mr-2" />
                      {t('financial.reject_payout', 'Reject Payout')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberPayouts;
